import Vue from 'vue';
import api from '../../helper/api';
import store from '../index';
import db from '../persistence/db';
import config from '../../config';
import { mergeDeep, cloneObject } from 'smartdoors-shared/js/helpers/common';

/**
 * Processes incoming raw job data
 * 
 * @param {array} jobData - The raw job data to process
 */
const processJobData = function (jobData) {
    const options = store.state.options;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const datesWithRequests = [];
    const datesWithRequestsPerStatus = {
        [options.JobTechnicianRequest.STATUS.requested]: [],
        [options.JobTechnicianRequest.STATUS.accepted]: [],
        [options.JobTechnicianRequest.STATUS.rejected]: []
    };
    const datesPerStatus = {
        [options.JobDate.STATUS.open]: [],
        [options.JobDate.STATUS.planned]: [],
        [options.JobDate.STATUS.inprocess]: [],
        [options.JobDate.STATUS.done]: []
    };
    let hasValidRequests = false;

    jobData.id = parseInt(jobData.id);
    jobData.dates = Object.values(jobData.dates);

    if (jobData.dates.length == 0) {
        jobData.delete = true;
        return jobData;
    }

    jobData.dates.forEach(date => {
        if (!Array.isArray(date.technician_requests) || date.technician_requests.length == 0) {
            // if there is a date without any technician requests, it means that the job data came from a cancelled/revoked notification
            // so we mark the date as cancelled (add css class) and set the technicianRequest property to null
            date.classes = ['cancelled'];
            date.technicianRequest = null;
        } else {
            // if there are requests we set the technicianRequest property to the request for the current logged in user
            // this is just an additional check for safety since there should come only one request for the logged in user from the api anyways

            date.technicianRequest = date.technician_requests.find(technicianRequest => technicianRequest.user_id == store.state.user.data.id);

            if (date.technicianRequest) {
                // proceed only if there has been a request for the current user found (there may be other requests coming from notifications)

                // determine and set validUntil property based on updated_at and acceptance_period from the request
                date.technicianRequest.validUntil = new Date(date.technicianRequest.updated_at);
                date.technicianRequest.validUntil.setHours(date.technicianRequest.validUntil.getHours() + date.technicianRequest.acceptance_period);
                // check if the request is valid (used below for checking if the job should be stored persistent)
                if (date.technicianRequest.validUntil >= (new Date())) {
                    hasValidRequests = true;
                }
                // add it to the datesWithRequests array
                datesWithRequests.push(date);
                // add it to status array
                datesWithRequestsPerStatus[date.technicianRequest.status].push(date);
            }
        }

        datesPerStatus[date.status].push(date);

        date.startDate = new Date(date.start);
        date.endDate = new Date(date.end);

        const dateEnd = new Date(date.end);
        dateEnd.setDate(dateEnd.getDate() + 1);
        date.isToday = (new Date(date.start)).setHours(0, 0, 0, 0) <= today && dateEnd.setHours(0, 0, 0, 0) > today;
    });

    if (jobData.status !== 0 && jobData.status < 20) {
        // only update job status if it isn't a temporary one (0) and it isn't closed (>= 20)
        if (datesWithRequestsPerStatus[options.JobTechnicianRequest.STATUS.requested].length) {
            // if there is an open request the job status for the current logged in technician is 10 (inquired)
            jobData.status = 10;
        } else if (datesPerStatus[options.JobDate.STATUS.inprocess].length) {
            // if there is a date with status 'inprocess' the job status should also be 'inprocess'.
            jobData.status = 12;
        } else if (!datesWithRequests.length) {
            // if there is no date with a request we set the job status to -1 so no button will be shown
            // this may be the case if all requests got cancelled/revoked and the job data came from the notification
            jobData.status = -1;
        } else if (datesWithRequestsPerStatus[options.JobTechnicianRequest.STATUS.accepted].length == datesWithRequests.length) {
            // if every request has status accepted the job status for the current logged in technician is 11 (planned)
            jobData.status = 11;
        }
    }

    // set hasDateToday property if today is in the range of a date
    jobData.hasDateToday = datesWithRequests.some(date => date.isToday) ? 1 : 0;

    // check if job should be stored persistent
    jobData.persist = true;

    if (jobData.status <= 0
        || (jobData.status == 20 && (new Date(jobData.created_at)).setHours(0, 0, 0, 0) < today.setMonth(today.getMonth() - 3))
        || (jobData.status == 10 && !hasValidRequests)) {
        // do not persist job if:
        // - status is <= 0 
        // - status is 20 (finished) and job is older than 3 months
        // - status is 10 (inquired) but there is no valid request
        jobData.persist = false;
    }

    return jobData;
}



/**
 * The jobs store module.
 * 
 * This is the most complex store module since we have to manage the following:
 * 
 * - Jobs which are loaded from the api should always be stored persistent on the client (indexedDB)
 * - Not all loaded jobs are always used for listing or detail views
 * - If a job gets updated, it should be updated in the persistent storage as well as in the current loaded or temporary stacks
 * - We may want that job changes get handled differently for each stack
 *      e.g: A job update should be applied to the 'current'-stack only if the job is currently within this stack, but since the new data has to be persistent, it has to be added to the 'loaded'-stack even if its not currently in there.
 *      So we have an update-only situation for the 'current'-stack but an update-or-insert situation for the 'loaded' stack.
 * - Jobs which met specific criteria have to be deleted from the persistent storage
 * - There may be jobs which should not be stored persistent (because it mets the criteria mentioned in the previous point) and are not currently in use by listings or a detail view, but should be available (e.g. for notification actions)
 * - Jobs must be filter- and searchable through the API and the persistent storage
 * - Jobs should not be restored by the vuex persitence plugin on load because we load it on demand with optional filtering and searching
 * - There has to be logic for adding, replacing, updating and deleting jobs
 * - Since there may be a lot of changes in a row, not every change should trigger a update in the persistent client storage
 * - The job status for the app user may be different from the status coming from the API
 * 
 * We deal with this with following key-logic:
 * 
 * - Jobs are partitioned in three stacks:
 *  - The "loaded" stack is for jobs which should be stored persistent but not currently used in any listing or detail view
 *  - The "current" stack holds the jobs which are currently used in a listing or detail view
 *  - The "temp" stack is for jobs which should not be persistent and are not currently used in a listing or detail view but may be needed
 * 
 * - There is a commit system for mutations: 
 *  - All changes should be made through the "addMutation"-action and commited through the "commitMutations"-mutation
 *  - The "commitMutations"-mutation will handle all previous added mutations, create lists of jobs which has been added, updated or deleted and trigger an update of the persistent client storage
 *  - There are some actions which will commit the mutation directly, used for situations where not many changes happen in a row
 * 
 * - There is a 'processJobData' function which processes new job data and sets some properties like the status and if the job should be stored persistent or not
 */
export default {
    settings: {
        persist: {
            storageKeyPathMapping: {
                'jobsOverride': ['override']
            }
        },
        persistDistinct: {
            // The 'persistPut' state property shall be used for writing the new data to the persistent storage
            persistPut: {
                storage: 'db',
                storageKey: 'jobs',
                useStateKey: false,
                mode: 'list',
                mutations: ['commitMutations']
            },
            // The 'persistDelete' state property shall be used for deleting jobs from the persistent storage
            persistDelete: {
                storage: 'db',
                storageKey: 'jobs',
                useStateKey: false,
                mode: 'deleteItems',
                mutations: ['commitMutations']
            },
            // The 'persistSync' state property shall be used for syncing jobs with the persistent storage 
            // (update or insert the jobs in this state property and delete all jobs which are in the persistent storage but not in this list)
            persistSync: {
                storage: 'db',
                storageKey: 'jobs',
                useStateKey: false,
                mode: 'syncList',
                mutations: ['syncPersistent']
            },
            override: {
                storage: 'db',
                storageKey: 'jobsOverride',
                useStateKey: false,
                mode: 'syncList',
                mutations: ['commitMutations']
            },
        }
    },

    module: {
        namespaced: true,

        state: {
            persistPut: [], // list of jobs that should be put in the persistent storage, used internally by commitMutations
            persistDelete: [], // list of jobs that should be deleted from the persistent storage, used internally by commitMutations
            persistSync: [], // list of jobs to sync with the persist storage - means that all jobs which are not in this list get deleted from the persist storage, others get updated
            stacks: {
                loaded: [], // this stack holds jobs that should be persistent but are not currently displayed for the user
                current: [], // this stack should be used for displaying jobs
                temp: [] // this stack holds jobs that are not currently displayed for the user and should not be persistent
            },
            stacksSettings: {
                loaded: {
                    persist: true // changes in the loaded stack should be saved to persistent storage
                },
                current: {
                    persist: true // changes in the current stack should be saved to persistent storage
                },
                temp: {
                    persist: false // changes in the temp stack should not be saved to persistent storage
                }
            },
            override: [], // persistent job data which should always override other loaded job data
            mutations: [], // uncommited mutations
            sync: {}, // sync payload used for syncing when syncPersitent mutation gets commited
            fileStructure: {},
            filesPromises: [],
            files: [],
            files_url: [],
            result_files_url: [],
        },
        getters: {
            file_urls: state => {
                return state.result_files_url.length > 0 ? state.result_files_url : [];
            },
        },
        mutations: {
            /**
             * Handles and commits all previous added mutations.
             * Sets the lists of jobs to put or delete on the persistent storage (persistPut and persistDelete).
             * This is the only mutation which will trigger a persistent storage update (Defined in the module settings above).
             * 
             * @param {Object} state 
             */
            commitMutations(state) {
                const persist = {
                    put: {},
                    delete: {},
                    override: Object.fromEntries(state.override.map(job => { return [job.id, job]; }))
                };
                let parsOver = [];
                parsOver.push(persist.override);


                for (const mutationPayload of state.mutations) {
                    if ('deleteOverride' in mutationPayload) {
                        const deleteOverrideKey = 'overrideKey' in mutationPayload ? mutationPayload.overrideKey : null;

                        for (const deleteId of mutationPayload.deleteOverride) {
                            if (deleteId in persist.override) {
                                if (deleteOverrideKey && deleteOverrideKey in persist.override[deleteId]['overrides']) {
                                    delete persist.override[deleteId]['overrides'][deleteOverrideKey];
                                } else {
                                    delete persist.override[deleteId];
                                }

                                if (Object.values(persist.override[deleteId]['overrides']).length == 0) {
                                    delete persist.override[deleteId];
                                }
                            }
                        }
                    }

                    if (mutationPayload.clearStacks) {
                        for (const stack of mutationPayload.stacks) {
                            state.stacks[stack] = [];
                        }
                    }

                    if ('items' in mutationPayload) {
                        const insert = mutationPayload.insert;
                        const override = 'override' in mutationPayload ? mutationPayload.override : false;

                        for (let job of mutationPayload.items) {
                            let jobUpdateData = mutationPayload.update ? job : null;
                            job = insert || mutationPayload.replace ? processJobData(job) : job;

                            if (override) {
                                const overrideKey = 'overrideKey' in mutationPayload ? mutationPayload.overrideKey : 'undefined';

                                if (!(job.id in persist.override)) {
                                    persist.override[job.id] = {
                                        id: job.id,
                                        overrides: {}
                                    };
                                }

                                if (overrideKey in persist.override[job.id]['overrides']) {
                                    persist.override[job.id]['overrides'][overrideKey] = mergeDeep(persist.override[job.id]['overrides'][overrideKey], job, false, ['id'], Vue.set);
                                } else {
                                    persist.override[job.id]['overrides'][overrideKey] = job;
                                }

                            } else {
                                const overrideDataIndex = state.override.findIndex(p => p.id == job.id);
                                if (overrideDataIndex != -1) {
                                    if (('parts' in job)) {
                                        Object.values(state.override[overrideDataIndex]['overrides']).forEach(overrideData => {
                                            job = mergeDeep(job, overrideData, false, ['id'], Vue.set);
                                        });
                                    }

                                    if (!mutationPayload.update) {
                                        job = processJobData(job);
                                    }
                                }
                            }

                            for (const stack of mutationPayload.stacks) {
                                const jobIndex = mutationPayload.replace || mutationPayload.update ? state.stacks[stack].findIndex(p => p.id == job.id) : -1;
                                let used = false;

                                if (jobIndex == -1 && insert) {
                                    state.stacks[stack][mutationPayload.insertMethod || 'push'](job);
                                    used = true;
                                } else if (jobIndex != -1) {
                                    if (mutationPayload.update) {
                                        job = processJobData(mergeDeep(state.stacks[stack][jobIndex], jobUpdateData, false, ['id'], Vue.set));
                                        state.stacks[stack].splice(jobIndex, 1, job);
                                        used = true;
                                    } else if (mutationPayload.replace) {
                                        state.stacks[stack].splice(jobIndex, 1, job);
                                        used = true;
                                    }
                                }

                                if (jobIndex != -1 && job.delete) {
                                    state.stacks[stack].splice(jobIndex, 1);
                                    job.persist = false;
                                }

                                if (used && state.stacksSettings[stack].persist) {
                                    if (!job.persist) {
                                        persist.delete[job.id] = job;
                                    } else {
                                        persist.put[job.id] = job;
                                    }
                                }

                                if (used && !job.persist && job.id in persist.override) {
                                    delete persist.override[job.id];
                                }
                            }
                        }
                    }
                }
                let parsOver2 = [];
                parsOver2.push(persist.override);
                state.mutations = [];
                state.persistPut = Object.values(persist.put);
                state.persistDelete = Object.keys(persist.delete).map(id => parseInt(id));
                state.override = Object.values(persist.override);
            },

            /**
             * Sync the persistent jobs storage with a given list of jobs.
             * Jobs which are in the persistent storage but not in this list will get deleted from the persistent storage, others will get updated or added.
             * 
             * @param {Object} state 
             * @param {Array} jobs 
             */
            syncPersistent(state) {
                const jobs = 'items' in state.sync ? state.sync.items : [];

                for (let i = 0; i < jobs.length; i++) {
                    const job = jobs[i];

                    const overrideDataIndex = state.override.findIndex(p => p.id == job.id);
                    if (overrideDataIndex != -1) {
                        Object.values(state.override[overrideDataIndex]['overrides']).forEach(overrideData => {
                            job = mergeDeep(job, overrideData, false, ['id'], Vue.set);
                        });
                    }

                    jobs[i] = processJobData(job);
                }

                if ('stacks' in state.sync) {
                    for (const stack of state.sync.stacks) {
                        state.stacks[stack] = jobs;
                    }
                }

                state.persistSync = jobs;
            }
        },

        actions: {
            /**
             * Loads jobs from remote endpoint (API), store state or persistent client storage (IndexedDB).
             * Payload can have following properties:
             * - {Object} params: 
             * 
             * @param {Object} context
             * @param {Object} payload Payload object
             */
            async load({ rootState, state, dispatch }, payload) {
                const filter = payload.params.filter ? payload.params.filter : {};
                const sources = payload.sources ? payload.sources : ['api', 'stacks', 'db']; // 'stacks' is only used when loading a single job
                let jobs = [];
                let synced = false;
                if ('id' in filter && (!filter.id || Object.keys(filter).length > 2)) {
                    // delete id filter (= get single job) if there is no valid id value set or if there are other filters set since they can't be combined with the id filter
                    delete filter.id;
                }

                if ('id' in filter) {
                    // get single job if id filter is set
                    const singleResult = await dispatch('loadSingle', Object.assign(payload, { sources: sources, id: filter.id }));

                    if (singleResult) {
                        jobs = [singleResult];
                    }

                    return jobs;
                } else {
                    delete filter.id; // remove id filter completely since even an empty id filter value would break the db search

                    if (!rootState.offline && sources.includes('api')) {
                        // get from api if online
                        store.dispatch("loadingPwa", 1);

                        const response = await api.get(payload.path, payload.params).run();

                        jobs = response.valueOf();



                        if ('isFiltered' in payload && !payload.isFiltered) {
                            // if the current load is unfiltered, we can sync the data with the persistent storage (means we are deleting every job from the persistent storage which is not in this result)
                            dispatch('syncAndCommit', {
                                items: jobs,
                                stack: 'targetStack' in payload ? payload.targetStack : null,
                                commit: 'commitPayload' in payload ? payload.commitPayload : {}
                            });
                            synced = true;
                        } else if (!('targetStack' in payload) || !state.stacksSettings[payload.targetStack].persist) {
                            // add to loaded stack and commit for writing new job data from api into client persistent storage
                            dispatch('addAndCommit', { items: jobs, stacks: ['loaded'] });
                        }

                        //clear all old structure
                        state.fileStructure = {};
                        const loadDocs = payload.disableLoadDocs ? payload.disableLoadDocs : false
                        if (loadDocs) {
                            dispatch('addDocumentToLocalDb', {
                                items: jobs
                            });
                        } else {
                            store.dispatch("loadingPwa", 2);
                        }

                    } else if (sources.includes('db')) {
                        // search in db if offline
                        const searchString = payload.params.search ? payload.params.search : null;

                        await db.ready;
                        jobs = await db.findInList('jobs', filter, searchString, [
                            'id_extern',
                            'locations.0.city',
                            'locations.0.street',
                            'locations.0.postal_code',
                            'locations.0.country'
                        ]);
                    }
                }

                if (!synced && 'targetStack' in payload) {
                    // load jobs into target stack
                    dispatch('setStackAndCommit', {
                        stack: payload.targetStack,
                        items: jobs,
                        commit: 'commitPayload' in payload ? payload.commitPayload : {}
                    });
                }

                return jobs;
            },

            /**
             * Load a single job
             * Trys sources in this order: API, store state stacks, persistent storage (IndexedDB), store state temp stack
             * 
             * @param {Object} context
             * @param {Number|Object} idOrPayload The id of the job to get or a payload object with id and targetStack as properties
             */
            async loadSingle({ rootState, state, dispatch }, idOrPayload) {
                const payload = typeof idOrPayload == 'object' ? idOrPayload : { id: idOrPayload };
                const sources = payload.sources ? payload.sources : ['api', 'stacks', 'db', 'temp'];
                const id = payload.id;
                let job = null;

                // first look in the temp stack
                if (sources.includes('temp')) {
                    job = state.stacks.temp.find(p => p.id == id);
                }

                if (!job && !rootState.offline && sources.includes('api')) {
                    // try to get job from api if online
                    try {
                        store.dispatch("loadingPwa", 1);

                        const response = await api.get(config.api.urls.jobs.listSingle(id)).run();
                        job = response.valueOf();

                        if (!('targetStack' in payload) || !state.stacksSettings[payload.targetStack].persist) {
                            // add to loaded stack and commit for writing new job data from api into client persistence storage
                            dispatch('addAndCommit', { items: [job], stacks: ['loaded'] });
                        }
                        store.dispatch("loadingPwa", 2);

                    } catch (err) {
                        console.warn('Warning, an API request failed: ' + err);
                    }
                }

                // search in store state stacks
                if (!job && sources.includes('stacks')) {
                    const stacks = ['current', 'loaded'];
                    for (const stack of stacks) {
                        const item = state.stacks[stack].find(p => p.id == id);

                        if (item) {
                            job = item;
                            break;
                        }
                    }
                }

                // try db
                if (!job && sources.includes('db')) {
                    try {
                        await db.ready;
                        job = await db.getItem('jobs', id);
                    } catch (err) {
                        console.warn('Warning, an IndexedDB Request failed: ' + err);
                    }
                }

                if (job && 'targetStack' in payload) {
                    // load job into target stack
                    dispatch('setStackAndCommit', {
                        stack: payload.targetStack,
                        items: [job],
                        commit: 'commitPayload' in payload ? payload.commitPayload : {}
                    });
                }

                return job;
            },


            /**
             * Add a file response to save in local database
             * If a dokument is in tha jop position id will be replaced
             * 
             * @param {Object} context
             * @param {Object} payload Mutation payload
            */
            async addDocumentToLocalDb({ dispatch, state }, payload) {
                store.dispatch("loadingPwa", 1);

                payload.items.forEach((item) => {
                    if (item.files) {
                        item.files.forEach(f => {
                            dispatch('getBase64', {
                                file: f.route,
                                fileId: f.id
                            });
                        })
                    }

                    item.parts.forEach((part) => {
                        if (part.files) {
                            part.files.forEach(pf => {
                                dispatch('getBase64', {
                                    file: pf.route,
                                    fileId: pf.id
                                });
                            })
                        }

                        if (part.fittings && part.fittings.length > 0) {

                            part.fittings.forEach(itemfitting => {
                                if (itemfitting.files && itemfitting.files.length > 0) {
                                    itemfitting.files.forEach(pft => {
                                        dispatch('getBase64', {
                                            file: pft.route,
                                            fileId: pft.id
                                        });
                                    })
                                }
                            })
                        }

                        if (part.location_files.length > 0) {
                            part.location_files.forEach(ppf => {
                                dispatch('getBase64', {
                                    file: ppf.route,
                                    fileId: ppf.id
                                });
                            })
                        }

                        if (part.report && part.report.pictures.length > 0) {
                            part.report.pictures.forEach(pic => {
                                dispatch('getBase64', {
                                    file: pic.route,
                                    fileId: pic.route,
                                    api: true
                                });
                            })
                        }

                        if (part.report && part.report.images) {
                            for (const [key, value] of Object.entries(part.report.images)) {
                                if (value.length > 0) {
                                    value.forEach(pic => {
                                        if (pic.route) {
                                            dispatch('getBase64', {
                                                file: pic.route,
                                                fileId: pic.route,
                                                api: true
                                            });
                                        }
                                    })
                                }
                            }
                        }


                    })
                    if (item.messages_technician && item.messages_technician.length > 0) {
                        item.messages_technician.forEach(m => {
                            if (m.data && m.data.pictures && m.data.pictures.length > 0)
                                m.data.pictures.forEach(mp => {
                                    if (mp.route) {
                                        dispatch('getBase64', {
                                            file: mp.route,
                                            fileId: mp.route,
                                            api: true
                                        });
                                    }
                                })
                        })
                    }

                });

                Promise.all(state.filesPromises).then(async () => {
                    store.dispatch("loadingPwa", 2);
                    try {
                        await db.ready;
                        await db.syncList('files', state.files);
                    } catch (err) {
                        console.log('Warning, an IndexedDB Request failed: ' + err);
                    }
                }).catch(err => {
                    store.dispatch("loadingPwa", 2);
                    reject(err);
                })
            },

            async getBase64({ dispatch, state }, payload) {
                let file = payload.file //.replace(/^http:\/\//i, 'https://');
                let id = payload.fileId
                let forApi = payload.api ? true : false;
                if (file) {
                    let path = process.env.VUE_APP_PORTAL_URL + '/files/show/' + file;
                    let path_download = process.env.VUE_APP_PORTAL_URL + (forApi ? '/api' : '') + '/files/download/' + file;
                    // path_download = process.env.VUE_APP_PORTAL_URL + '/files/show/Job/151/files/0';
                    let fileStructureObj = state.fileStructure;

                    if (!fileStructureObj.hasOwnProperty(id)) {
                        state.fileStructure[id] = 'init';
                        // const promiseFile = pdf2base64(path)
                        const promiseFile = new Promise((resolve, reject) => {
                            var xhr = new XMLHttpRequest(),
                                blob;

                            xhr.open("GET", path_download, true);
                            if (forApi) {
                                let token = store.state.user.accessToken;
                                if (token) {
                                    xhr.setRequestHeader('Authorization', 'Bearer ' + token);
                                }
                            }
                            // Set the responseType to blob
                            xhr.responseType = "blob";

                            xhr.addEventListener("load", async function () {
                                if (xhr.status === 200) {
                                    // File as response
                                    blob = xhr.response;
                                    state.files.push({ id: id + "", value: blob })
                                    state.fileStructure[id] = blob //base64 type file
                                    resolve();

                                } else {
                                    resolve();
                                }
                            }, false);
                            // Send XHR
                            xhr.send();
                        });
                        state.filesPromises.push(promiseFile);
                    }
                }
            },


            /**
             * Adds a list of items to the defined stacks and commits the mutations.
             * If a job is already in one of the stacks, it will be replaced.
             * If no stacks are defined in the payload, the 'loaded' stack will be used.
             * 
             * @param {Object} context
             * @param {Object} payload Mutation payload
             */
            addAndCommit({ dispatch }, payload) {
                dispatch('commitMutation', Object.assign({
                    stacks: ['loaded'],
                    insert: true,
                    replace: true
                }, payload));
            },

            /**
             * Clears a stack and fills it with new items.
             * Commits the mutation afterwards.
             * 
             * @param {Object} context
             * @param {Object} payload Mutation payload with additional 'stack' property for targeting a single stack
             */
            setStackAndCommit({ dispatch }, payload) {
                dispatch('commitMutation', Object.assign({
                    stacks: ['stack' in payload && payload.stack ? payload.stack : 'loaded'],
                    clearStacks: true,
                    insert: true
                }, payload));
            },

            /**
             * Adds an mutation and commit it directly afterwards.
             * If the payload does not have a 'mutation' property, it will be directly used as the mutation payload.
             * If the mutation payload has a 'commit' property, it will be used as the commit payload and deleted from the mutation payload.
             * 
             * @param {Object} context
             * @param {Object} payload Payload object with a 'mutation' (payload for mutation) property and an optional 'commit'-property (payload for commit)
             */
            commitMutation({ commit, dispatch }, payload) {
                if (!('mutation' in payload)) {
                    payload = {
                        mutation: payload
                    };
                }

                if ('commit' in payload.mutation) {
                    payload.commit = payload.mutation.commit;
                    delete payload.mutation.commit;
                }

                dispatch('addMutation', payload.mutation);
                commit('commitMutations', 'commit' in payload ? payload.commit : {})
            },

            /**
             * Set a job persistently without commiting the mutation.
             * Will add it to, or replace it in the loaded stack for persistence, and replace it in other stacks which already contains the job
             * 
             * @param {Object} context 
             * @param {Object} job The job data
             */
            set({ dispatch }, job) {
                const items = Array.isArray(job) ? job : [job];

                // add or replace in loaded stack (so we make sure that its still persistent even if the job is not contained in another persist stack)
                dispatch('addMutation', {
                    stacks: ['loaded'],
                    items: items,
                    insert: true,
                    replace: true
                });
                // replace only in other stacks
                dispatch('addMutation', {
                    stacks: ['current', 'temp'],
                    items: items,
                    insert: false,
                    replace: true
                });
            },

            /**
             * Set a job persistently and commit the mutation.
             * Will add it to, or replace it in the loaded stack for persistence, and replace it in other stacks which already contains the job
             * 
             * @param {Object} context 
             * @param {Object} job The job data
             */
            setAndCommit({ dispatch, commit }, job) {
                dispatch('set', job);
                commit('commitMutations');
            },

            /**
             * Sync the persistent jobs storage with a given list of jobs.
             * Jobs which are in the persistent storage but not in this list will get deleted from the persistent storage, others will get updated or added.
             * 
             * @param {Object} context 
             * @param {Array} jobsOrPayload List of jobs or a payload object with key 'items' and optional keys 'stack' (for setting the new list also to a stack) and 'commit' for a commit payload.
             */
            syncAndCommit({ commit, state }, jobsOrPayload) {
                const syncPayload = Array.isArray(jobsOrPayload) ? { items: jobsOrPayload } : jobsOrPayload;

                state.sync = Object.assign({
                    stacks: 'stack' in syncPayload && syncPayload.stack ? [syncPayload.stack] : [],
                }, syncPayload);

                commit('syncPersistent', 'commit' in syncPayload ? syncPayload.commit : {})
            },

            /**
             * Set a job temporary, so it can be used for displaying but will not be stored persistently
             * 
             * @param {Object} context 
             * @param {Object} job The job data
             */
            setTemp({ dispatch }, job) {
                dispatch('addMutation', {
                    stacks: ['temp'],
                    items: [job],
                    insert: true,
                    replace: true
                })
            },

            /**
             * Update job data for a job in all stacks which contains the job.
             * The 'id' property has to be set in addition to the data for udpating.
             * 
             * @param {Object} context
             * @param {Object} job The job Data
             */
            update({ dispatch }, job) {
                dispatch('addLocalMutation', {
                    items: [job],
                    insert: false,
                    replace: false,
                    update: true
                });
            },

            /**
             * Update job data on the remote endpoint and in all local stacks which contains the job.
             * The 'id' property has to be set in addition to the data for udpating.
             * 
             * @param {Object} context 
             * @param {Object} job The job data
             */
            updateRemote({ dispatch }, job) {
                return dispatch('addRemoteMutation', {
                    items: [job],
                    insert: false,
                    replace: false,
                    update: true
                });
            },

            /**
             * Adds job data to the override list - will always override (merged into) job data loaded from other sources.
             * Also updates the job with the given data in all stacks.
             * 
             * @param {Object} context
             * @param {Object} overrideData The override data. 'id' key must be present along with the override data.
             */
            addOverride({ state, dispatch }, payload) {
                dispatch('addMutation', {
                    items: 'items' in payload ? payload.items : [payload.data],
                    stacks: ['loaded', 'current'],
                    insert: false,
                    replace: false,
                    update: true,
                    override: true,
                    overrideKey: payload.key
                });
            },

            /**
             * Deletes the override job data for the given job ids from the override list.
             * 
             * @param {Object} context
             * @param {Array|Number} ids 
             */
            deleteOverride({ dispatch }, payload) {
                payload = typeof payload == 'object' && !Array.isArray(payload) ? payload : { id: payload };

                dispatch('commitMutation', {
                    deleteOverride: Array.isArray(payload.id) ? payload.id : [payload.id],
                    overrideKey: 'key' in payload ? payload.key : null
                });
            },

            /**
             * Adds a mutation to the list of uncommited mutations (local) and/or apply it on the remote endpoint (remote).
             * Local changes will not be applied to the target stacks until the "commitMutations"-mutation gets called.
             * 
             * Possible payload properties are:
             * - {Boolean} local=true: If the mutation should be made local (on the client)
             * - {Boolean} remote=false: If the mutation should be made on the remote endpoint (send to api)
             * - {Array} stacks: The stacks the mutation should be applied to (local only)
             * - {Array} items: The job data items with the actual data to insert/update/replace
             * - {Boolean} insert: If an item should be inserted if its not already within the stack
             * - {String} insertMethod: The insert method ("push" or "unshift") (local only)
             * - {Boolean} insertRelations=true: If relations should be inserted if primary key is not set (remote only)
             * - {Boolean} replace: If an item should be replaced if its already within the stack
             * - {Boolean} update: If the item data should be merged if the job is already within the stack (id property has to be set on the item object along with the new data to merge)
             * - {Boolean} clearStacks: If the stacks should be cleared before adding the new items
             * - {Boolean} override: If the given items data should be added to the override list - will be used to override (merge into) job data loaded from other sources.
             * - {Array} deleteOverride: Array of job ids for which the override data should be deleted from the override list.
             * 
             * @param {Object} state 
             * @param {Object} payload The mutation payload with the properties needed by "commitMutations"
             */
            addMutation({ state, dispatch }, payload) {
                if ((!('local' in payload) && !('remote' in payload))) {
                    payload.local = true;
                }

                if (payload.local) {
                    if (!('stacks' in payload)) {
                        payload.stacks = Object.keys(state.stacks);
                    }

                    state.mutations.push(payload);
                }

                if (payload.remote) {

                    const mutationTask = api.post(config.api.urls.jobs.mutation, payload).name('jobUpdate');

                    return dispatch('tasks/run', mutationTask, { root: true }).thenAlways(['store', 'dispatch:jobs/setAndCommit']);
                }
            },

            /**
             * Adds a mutation to the list of uncommited mutations.
             * See action 'addMutation' for possible payload properties.
             * 
             * @param {Object} context
             * @param {Object} payload The mutation payload with the properties needed by "commitMutations"
             */
            addLocalMutation({ dispatch }, payload) {
                payload.local = true;

                return dispatch('addMutation', payload);
            },

            /**
             * Applys a mutation at the remote endpoint.
             * See action 'addMutation' for possible payload properties.
             * 
             * @param {Object} context
             * @param {Object} payload The mutation payload with the properties needed by "commitMutations"
             */
            addRemoteMutation({ dispatch }, payload) {
                payload.remote = true;
                return dispatch('addMutation', payload);
            },

            /**
             * Commit all uncommited mutations.
             * 
             * @param {Object} context
             * @param {*} payload Commit payload: May be used for identifying the commit on a store saved event handler
             */
            commit({ commit }, payload = null) {
                commit('commitMutations', payload);
            },

            /**
             * Respond to job technician requests
             * 
             * @param {Object} context
             * @param {Object} payload Payload object with 'job' (id of job or job object) and 'responses' (response statuses and reasons per date ids) properties.
             */
            async respondRequests({ dispatch }, payload) {
                const job = typeof payload.job == 'object'
                    ? payload.job
                    : await dispatch('loadSingle', { id: payload.job, sources: ['stacks', 'db'] });
                const jobDates = job.dates;

                job.status = 0;
                job.dates = [];

                for (const date of jobDates) {
                    if (date.id in payload.responses) {
                        const responseData = payload.responses[date.id];
                        date.technician_requests[0].status = responseData.status;

                        if (status == 3) {
                            date.technician_requests[0].reason_id = responseData.reason;
                        } else {
                            job.dates.push(date);
                        }
                    } else {
                        job.dates.push(date);
                    }
                }

                // set updated job data temporary until the respond-task is done
                dispatch('setAndCommit', job);

                const respondTask = api.post(config.api.urls.jobs.requestRespond(job.id), { responses: payload.responses });

                return dispatch('tasks/run', respondTask, { root: true }).thenAlways(['store', 'dispatch:jobs/setAndCommit']);
            },

            /**
             * Respond to check qr code
             * 
             * @param {Object} context
             * @param {Object} payload Payload object with scanned 'code'.
             */
            async respondQrCodeRequests({ dispatch }, payload) {

                const respondTask = api.post(config.api.urls.jobs.checkQrCode, { 'code': payload.code, 'part_id': payload.part_id });

                return await dispatch('tasks/run', respondTask, { root: true });
            },

            async updateFittings({ state, rootState, dispatch, commit }, payload) {
                if (rootState.offline) {
                    if (!('stacks' in payload)) {
                        payload.stacks = Object.keys(state.stacks);
                    }

                    state.mutations.push(payload);
                    return;
                }

                if (!rootState.offline) {
                    const updateTask = api.post(config.api.urls.jobs.updateFittings(payload.id), payload.data);

                    return dispatch('tasks/run', updateTask, { root: true }).thenAlways(['store', 'dispatch:jobs/setAndCommit']);
                }
            },

            async sendCommonTechnicianMessage({ rootState, dispatch }, payload) {

                const sendTask = api.post(config.api.urls.jobs.sendCommonTechnicianMessage(payload.id), { message: payload.message, part_id: payload.part_id ? payload.part_id : null });
                if (!rootState.offline) {
                    return dispatch('tasks/run', sendTask, { root: true }).thenAlways(['store', 'dispatch:jobs/setAndCommit']);
                } else {
                    return dispatch('tasks/run', sendTask, { root: true });
                }

            },

            async closeJob({ rootState, dispatch }, payload) {
                const requestParams = {};

                if ('message' in payload) {
                    requestParams.message = payload.message;
                }

                if ('rating' in payload) {
                    requestParams.rating = payload.rating;
                }

                dispatch('update', {
                    id: payload.id,
                    generate_pdf: payload.generate_pdf ? payload.generate_pdf : false,
                    status: payload.status
                });
                dispatch('commit');

                // if (!rootState.offline) {
                const closeTask = api.post(config.api.urls.jobs.close(payload.id, payload.status), requestParams);

                return dispatch('tasks/run', closeTask, { root: true });
            }
        }
    }
}